import { useMemo } from 'react';

import {
  formatProductDiscountText,
  getBogoFromProduct,
  getBogoSpecialURL,
  getBundleFromProduct,
  getTargetPriceDiscountsFromProduct,
} from '@/utils';
import { GProduct, Variant } from '@/types';
import { useKioskBasePath } from '@/kiosk/_hooks/useKioskBasePath';

import { useData } from '../components';

const useActiveProductBadges = ({
  product,
  weightVariant,
  isDetail = false,
  badgeClass,
  onKiosk,
}: ActiveBadgesParams) => {
  const { basePath } = useKioskBasePath();
  const { productCard } = useData();
  const bogo = getBogoFromProduct(product);
  const bundle = getBundleFromProduct(product);
  const targetPriceDiscounts = getTargetPriceDiscountsFromProduct(product);
  // IDs of discounts that were actually applied during OpenSearch price calculation
  const discountsAppliedIds = product?.pricing?.applied_discount_ids || [];
  // Find the TARGET_PRICE discount that was actually applied
  const targetPriceDiscountApplied = targetPriceDiscounts.find(discount =>
    discountsAppliedIds?.includes(discount.discount_id),
  );

  // Extract IDs of PERCENT and DOLLAR discounts (not TARGET_PRICE) for badge display validation
  const percentOrDollarDiscountIds = product?.discounts
    .filter(
      discount =>
        (discount?.discount_method === 'DOLLAR' ||
          discount?.discount_method === 'PERCENT') &&
        discount?.discount_affinity === 'Pre-Cart',
    )
    .map(discount => discount.discount_id);

  // Check if TARGET_PRICE discount was actually applied (important when non-stackable discounts compete)
  const isTargetPriceApplied = useMemo(
    () => Boolean(targetPriceDiscountApplied),
    [targetPriceDiscountApplied],
  );

  // Only use TARGET_PRICE discount amount if it was actually applied, otherwise 0
  const targetPriceDiscount = isTargetPriceApplied
    ? targetPriceDiscountApplied?.discount_amount
    : 0;

  const productDiscount = useMemo(
    () =>
      product.discounts.find(discount =>
        discountsAppliedIds.includes(discount.discount_id),
      ),
    [discountsAppliedIds, product.discounts],
  );
  const discountMethod = productDiscount?.discount_method;

  const productDiscountText = useMemo(() => {
    if (discountMethod === 'DOLLAR' && !!productCard?.dollarOffBadgeLabel) {
      return productCard?.dollarOffBadgeLabel;
    }

    if (discountMethod === 'PERCENT' && !!productCard?.percentOffBadgeLabel) {
      return productCard?.percentOffBadgeLabel;
    }

    return formatProductDiscountText({
      format: productCard?.discountDisplay ?? 'smart',
      value: weightVariant,
      targetPrice: targetPriceDiscount,
      discountMethod,
    });
  }, [
    weightVariant,
    productCard?.discountDisplay,
    targetPriceDiscount,
    discountMethod,
    productCard?.dollarOffBadgeLabel,
    productCard?.percentOffBadgeLabel,
  ]);

  // Validate that at least one PERCENT/DOLLAR discount was actually applied before showing badge
  const isDiscountTextApplied = useMemo(
    () =>
      !!productDiscountText &&
      discountsAppliedIds?.some(discountId =>
        percentOrDollarDiscountIds?.includes(discountId),
      ),
    [productDiscountText, discountsAppliedIds, weightVariant?.id],
  );

  const hasStockFlags = !!(
    product?.isLowStock ||
    product?.isOutStock ||
    productDiscountText
  );

  const hasPromotions = !!(bogo || bundle);

  const showPromoBadges = !isDetail && hasPromotions;

  const shouldRender =
    hasStockFlags || showPromoBadges || !!targetPriceDiscounts.length;

  const badges = useMemo(() => {
    const bogoSpecialUrl = bogo ? getBogoSpecialURL(bogo) : undefined;
    const bundleSpecialUrl = bundle ? getBogoSpecialURL(bundle) : undefined;
    const bogoSpecialLink = bogo
      ? `${onKiosk ? `${basePath}/shop` : ''}${bogoSpecialUrl}`
      : undefined;
    const bundleSpecialLink = bundle
      ? `${onKiosk ? `${basePath}/shop` : ''}${bundleSpecialUrl}`
      : undefined;

    const allBadgeConfigs: (BadgeConfig & { condition: boolean })[] = [
      {
        condition: !!product.isLowStock,
        props: {
          key: 'badge-low-stock',
          label: 'Low Stock',
          type: 'warning',
        },
      },
      {
        condition: !!product.isOutStock,
        props: {
          key: 'badge-out-of-stock',
          label: 'Out of Stock',
          type: 'warning',
        },
      },
      {
        condition: !isDetail && !!bogo,
        props: {
          key: 'badge-bogo',
          label: productCard?.bogoBadgeLabel ?? 'BOGO',
          type: 'bogo',
          iconName: 'gift',
          link: bogoSpecialLink,
          showIcon: true,
        },
      },
      {
        condition: !isDetail && !!bundle,
        props: {
          key: 'badge-bundle',
          label: productCard?.bundleBadgeLabel ?? 'BUNDLE',
          type: 'bogo',
          iconName: 'price-tag',
          link: bundleSpecialLink,
          showIcon: true,
        },
      },
      {
        condition: !!isDiscountTextApplied,
        props: {
          key: 'badge-discount',
          label: productDiscountText || '',
          type: 'discount',
        },
      },
      {
        condition: !!isTargetPriceApplied,
        props: {
          key: 'badge-target-price',
          label: `$${targetPriceDiscountApplied?.discount_amount || 0} price`,
          type: 'discount',
        },
      },
    ];

    return allBadgeConfigs
      .filter(badge => badge.condition)
      .map(({ props }) => ({
        props: {
          ...props,
          className: badgeClass,
        },
      }));
  }, [
    product.isLowStock,
    product.isOutStock,
    bogo,
    bundle,
    targetPriceDiscountApplied,
    productDiscountText,
    isDetail,
    badgeClass,
    productCard?.bogoBadgeLabel,
    productCard?.bundleBadgeLabel,
  ]);

  return {
    shouldRender,
    badges,
  };
};

interface ActiveBadgesParams {
  product: GProduct;
  weightVariant: Variant;
  isDetail?: boolean;
  badgeClass?: string;
  onKiosk?: boolean;
}

interface BadgeConfig {
  props: {
    className?: string;
    key: string;
    label: string;
    type: 'warning' | 'bogo' | 'discount';
    iconName?: 'gift' | 'price-tag';
    link?: string;
    showIcon?: boolean;
  };
}

export { useActiveProductBadges };
